import { AlertDialog, Button, Callout, Flex } from '@radix-ui/themes'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { RewardSchedulesAPI } from '@API/data-services/reward-schedules-service'
import { readErrorMessage } from '@Components/util/utils'
import { RewardScheduleWithGame } from '@Generated/back-office-client/src'
import { PRIZE_SCHEDULES_QUERY_KEY } from './types'

const END_FAILED_MESSAGE = 'Could not end the prize schedule.'

export const EndEarlyButton = ({ schedule }: { schedule: RewardScheduleWithGame }) => {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [endError, setEndError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => RewardSchedulesAPI.endEarly(schedule.id),
    onSuccess: async () => {
      setOpen(false)
      await queryClient.invalidateQueries({ queryKey: PRIZE_SCHEDULES_QUERY_KEY })
    },
    onError: async (error) => setEndError(await readErrorMessage(error, END_FAILED_MESSAGE)),
  })

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        setEndError(null)
      }}>
      <AlertDialog.Trigger>
        <Button color="red" variant="soft" size="1">
          End early
        </Button>
      </AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="480px">
        <AlertDialog.Title>End {schedule.name} now?</AlertDialog.Title>
        <AlertDialog.Description size="2">
          Players stop seeing this schedule immediately, and prizes that haven&apos;t unlocked yet
          will never be awarded. This can&apos;t be undone.
        </AlertDialog.Description>

        {endError ? (
          <Callout.Root color="red" role="alert" mt="3">
            <Callout.Text>{endError}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray" disabled={mutation.isPending}>
              Cancel
            </Button>
          </AlertDialog.Cancel>
          {/* Not an AlertDialog.Action: that would close the dialog before the request finishes */}
          <Button color="red" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Ending...' : 'End schedule'}
          </Button>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}
